import { resolveConfig, type WatchOverConfig } from "./config.js";
import { WatchOverClient } from "./client.js";
import type { PropBag } from "@watchover/shared";

/**
 * Public façade. Methods are safe to call before init(): they buffer until the
 * client exists, so a host page can fire watchover.track(...) immediately after
 * loading the async script without races.
 */
class WatchOver {
  private client: WatchOverClient | null = null;
  private pre: Array<[keyof WatchOverClient, unknown[]]> = [];

  init(override?: Partial<WatchOverConfig>): WatchOver {
    if (this.client) return this; // idempotent
    const cfg = resolveConfig(override);
    if (!cfg) return this;
    this.client = new WatchOverClient(cfg);
    this.client.start();
    // Replay any calls made before init resolved.
    for (const [method, args] of this.pre) {
      (this.client[method] as (...a: unknown[]) => unknown)(...args);
    }
    this.pre = [];
    return this;
  }

  track(name: string, props?: PropBag) {
    this.call("track", [name, props]);
  }
  identify(userId: string, traits?: PropBag) {
    this.call("identify", [userId, traits]);
  }
  page(props?: PropBag) {
    this.call("page", [props]);
  }
  flush() {
    this.call("flush", []);
  }
  stop() {
    this.call("stop", []);
  }
  optOut() {
    this.call("optOut", []);
  }
  optIn() {
    this.call("optIn", []);
  }
  getSessionId(): string | null {
    return this.client?.getSessionId() ?? null;
  }
  getVisitorId(): string | null {
    return this.client?.getVisitorId() ?? null;
  }

  private call(method: keyof WatchOverClient, args: unknown[]) {
    if (this.client) {
      (this.client[method] as (...a: unknown[]) => unknown)(...args);
    } else {
      this.pre.push([method, args]);
    }
  }
}

export const watchover = new WatchOver();
export type { WatchOverConfig } from "./config.js";
export { WatchOver };

/**
 * Drain a command-queue stub of the form:
 *   window.wo = function(){ (wo.q = wo.q || []).push(arguments) }
 *   wo('init', {...}); wo('track', 'signup');
 * Replaces the stub with a real dispatcher.
 */
const DISPATCHABLE = new Set([
  "init",
  "track",
  "identify",
  "page",
  "flush",
  "stop",
  "optOut",
  "optIn",
  "getSessionId",
  "getVisitorId",
]);

export function installQueueDrain(globalName: string) {
  const w = window as any;
  const stub = w[globalName];
  const queued: IArguments[] = stub?.q ?? [];

  const dispatch = (...args: unknown[]) => {
    const [method, ...rest] = args as [string, ...unknown[]];
    // Allowlist only public API methods — never reach inherited/prototype props.
    if (!DISPATCHABLE.has(method)) {
      console.warn("[watchover] unknown method:", method);
      return;
    }
    const fn = (watchover as any)[method];
    if (typeof fn === "function") return fn.apply(watchover, rest);
  };
  w[globalName] = dispatch;
  for (const args of queued) dispatch(...(Array.from(args) as unknown[]));
}
